import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Award,
  Users,
  Leaf,
  ArrowRight
} from 'lucide-react';

const BrandPillars = () => {
  const [activePillar, setActivePillar] = useState(0);

  const pillars = [
    {
      icon: Award,
      title: "Unmatched Product Excellence",
      description: "Engineered for durability, fit, and protection. Crafted from 100% recycled materials—a feat no competitor matches.",
      features: [
        "American-made with faster production times",
        "Real cost savings with no customs headaches", 
        "Product quality is non-negotiable",
        "Deadlines are met, not negotiated"
      ],
      color: "automats-red",
      bgGradient: "from-red-50 to-red-100"
    },
    {
      icon: Users,
      title: "Real Human Relationships",
      description: "Business runs on real, human relationships—not faceless transactions. Every client gets personal attention.",
      features: [
        "Customers treated like partners",
        "Customization comes standard",
        "Personal follow-through guaranteed", 
        "Real people answer the phone"
      ],
      color: "automats-steel",
      bgGradient: "from-gray-50 to-gray-100"
    },
    {
      icon: Leaf,
      title: "Eco-Innovation & Operational Precision",
      description: "We don't 'do' greenwashing; we set the standard. Zero virgin content, 100% U.S. sourcing and production.",
      features: [
        "Turning national waste into national value",
        "Protecting both vehicles and environment",
        "No overseas delays or container waiting",
        "Processes built for business, not bureaucracy"
      ],
      color: "automats-eco-green",
      bgGradient: "from-green-50 to-green-100"
    }
  ];

  return (
    <section className="py-20 lg:py-24 bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-automats-red/10 text-automats-red border-automats-red/20 mb-4">
            The AutoMats Difference
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-semibold text-automats-black mb-6">
            Three Pillars of Excellence
          </h2>
          <p className="text-xl text-automats-black max-w-3xl mx-auto leading-relaxed">
            Our competitive advantage isn't built on one thing—it's the unique combination 
            of product excellence, human relationships, and environmental innovation.
          </p>
        </div>

        {/* Pillar Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {pillars.map((pillar, index) => (
            <button
              key={index}
              onClick={() => setActivePillar(index)}
              className={`px-8 py-4 rounded-xl font-medium transition-all duration-300 ${
                activePillar === index 
                  ? 'bg-automats-red text-white shadow-lg shadow-automats-red/30 transform -translate-y-0.5' 
                  : 'bg-white text-automats-black shadow-md shadow-gray-100 hover:shadow-lg hover:shadow-gray-200 hover:transform hover:-translate-y-0.5 border border-gray-100'
              }`}
            >
              {pillar.title}
            </button>
          ))}
        </div>

        {/* Active Pillar Content */}
        <Card className="overflow-hidden shadow-2xl shadow-gray-100 border-0 rounded-2xl">
          <CardContent className="p-0">
            <div className="grid lg:grid-cols-2 min-h-[500px]">
              {/* Content Side */}
              <div className="p-10 lg:p-12 flex flex-col justify-center bg-white">
                <div className="space-y-8">
                  <div className="flex items-center space-x-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-automats-red to-red-600 flex items-center justify-center shadow-lg shadow-automats-red/30">
                      {React.createElement(pillars[activePillar].icon, {
                        className: `h-8 w-8 text-white`
                      })}
                    </div>
                    <h3 className="text-4xl font-semibold text-automats-black">
                      {pillars[activePillar].title}
                    </h3>
                  </div>
                  
                  <p className="text-xl text-automats-black leading-relaxed opacity-80">
                    {pillars[activePillar].description}
                  </p>

                  <ul className="space-y-3">
                    {pillars[activePillar].features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start space-x-3">
                        <ArrowRight className={`h-5 w-5 text-${pillars[activePillar].color} mt-0.5 flex-shrink-0`} />
                        <span className="font-body text-automats-steel">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Visual Side */}
              <div className={`bg-gradient-to-br ${pillars[activePillar].bgGradient} flex items-center justify-center p-8`}>
                <div className="text-center space-y-6 max-w-sm mx-auto">
                  <div className="w-40 h-40 bg-white rounded-full flex items-center justify-center shadow-xl mx-auto">
                    {React.createElement(pillars[activePillar].icon, {
                      className: `h-20 w-20 text-${pillars[activePillar].color}`
                    })}
                  </div>
                  <div className="space-y-2">
                    <h4 className="text-2xl font-heading font-bold text-automats-black">
                      {pillars[activePillar].title.split(' ')[0]}
                    </h4>
                    <p className="font-body text-automats-steel text-center">
                      Premium industrial, eco-exceptional manufacturing that delivers results
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Premium CTA Section */}
        <div className="mt-20 text-center">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-5xl font-semibold text-automats-black mb-8 leading-tight">
              Experience All Three Pillars
            </h3>
            <p className="text-2xl text-automats-black mb-12 leading-relaxed font-light">
              Ready to see how product excellence, genuine relationships, and eco-innovation 
              can transform your business? Let's start the conversation.
            </p>
            <div className="flex justify-center">
              <a 
                href="mailto:sales@automats.us?subject=Sample Kit Request&body=Hello, I would like to request a sample kit to experience the AutoMats difference."
                className="btn-clean btn-premium text-white text-lg font-medium"
              >
                Request Sample Kit
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BrandPillars;